import { openDB } from 'idb';

/**
 * Binary storage for uploaded Font Resource files, mirroring imageDB.js's
 * design (same `idb` library, same open/transaction/CRUD shape) — see
 * docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md.
 *
 * Deliberately a separate, standalone module rather than a generalization of
 * imageDB.js: imageDB's actual complexity (the label/folder tree, image-size
 * probing) is image-specific and has nothing to do with fonts, so there's
 * nothing meaningful to share there. What IS shared between the two —
 * "open an IndexedDB database, CRUD one object store" — is a handful of
 * one-line `idb` calls; extracting a helper for that would add a layer of
 * indirection to serve exactly one additional caller, which is why this
 * stays self-contained instead.
 *
 * FontStore (src/stores/fontStore.js) owns *when* to call these functions
 * and holds the resulting `storageId`; it never touches font bytes itself
 * beyond passing them through here.
 */

const DB_NAME = 'font-store';
const FONT_FILE_STORE = 'fontFiles';

const getDB = () => openDB(DB_NAME, 1, {
    upgrade(db) {
        if (!db.objectStoreNames.contains(FONT_FILE_STORE)) {
            db.createObjectStore(FONT_FILE_STORE, { keyPath: 'id', autoIncrement: true });
        }
    },
});

/** Store a font file's bytes (as a base64 data URL) + its own metadata. Returns the new row's id. */
export const addFontFile = async (data, originalFilename, type) => {
    const db = await getDB();
    const tx = db.transaction(FONT_FILE_STORE, 'readwrite');
    const id = await tx.objectStore(FONT_FILE_STORE).add({ data, originalFilename, type });
    await tx.done;
    return id;
};

export const getFontFile = async (id) => {
    const db = await getDB();
    const tx = db.transaction(FONT_FILE_STORE, 'readonly');
    const record = await tx.objectStore(FONT_FILE_STORE).get(id);
    await tx.done;
    return record;
};

export const deleteFontFile = async (id) => {
    const db = await getDB();
    const tx = db.transaction(FONT_FILE_STORE, 'readwrite');
    await tx.objectStore(FONT_FILE_STORE).delete(id);
    await tx.done;
};
